import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock, FileText, Globe, Hash, MessageSquare } from "lucide-react";
import type { DeliveryChannel } from "@z-suite/shared";
import { useAuth } from "../lib/auth.js";
import { useSaveOnboarding, useTopics } from "../lib/queries.js";
import { Button, Field, FullPageSpinner } from "../components/ui.js";
import { TopicPicker } from "../components/TopicPicker.js";
import { Wordmark } from "../components/brand/Logo.js";

const STEPS = ["Profiliniz", "İlgi alanlarınız", "Teslimat"] as const;

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: topics, isLoading } = useTopics();
  const saveOnboarding = useSaveOnboarding();

  const [step, setStep] = useState(0);
  const [fullName, setFullName] = useState(
    (user?.user_metadata?.full_name as string | undefined) ?? "",
  );
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [channels, setChannels] = useState<Set<DeliveryChannel>>(new Set(["web"]));
  const [deliveryTime, setDeliveryTime] = useState("07:00");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canNext = useMemo(() => {
    if (step === 0) return fullName.trim() && company.trim() && role.trim();
    if (step === 1) return selected.size >= 1;
    return consent && channels.size >= 1;
  }, [step, fullName, company, role, selected, consent, channels]);

  function toggleTopic(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleChannel(c: DeliveryChannel) {
    setChannels((prev) => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });
  }

  async function finish() {
    if (!user) return;
    setError(null);
    try {
      await saveOnboarding.mutateAsync({
        userId: user.id,
        full_name: fullName.trim(),
        company: company.trim(),
        role: role.trim(),
        delivery_time: deliveryTime,
        delivery_channels: [...channels],
        topicIds: [...selected],
      });
      navigate("/", { replace: true });
    } catch (e) {
      setError((e as Error).message ?? "Kayıt sırasında bir hata oluştu.");
    }
  }

  if (isLoading) return <FullPageSpinner label="Konular yükleniyor…" />;

  const firstName = fullName.trim().split(" ")[0];

  return (
    <div className="mx-auto min-h-screen max-w-2xl px-6 py-10">
      <Wordmark className="mb-10" />

      {/* İlerleme */}
      <div className="mb-8 flex items-center gap-2">
        {STEPS.map((label, i) => (
          <div key={label} className="flex flex-1 items-center gap-2">
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs transition-colors ${
                i < step
                  ? "border-gold bg-gold text-ink-900"
                  : i === step
                    ? "border-gold text-gold"
                    : "border-slate-700 text-slate-500"
              }`}
            >
              {i < step ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
            </div>
            <span className={`hidden text-sm sm:inline ${i === step ? "text-slate-200" : "text-slate-500"}`}>
              {label}
            </span>
            {i < STEPS.length - 1 && <div className="h-px flex-1 bg-slate-700/50" />}
          </div>
        ))}
      </div>

      <div className="animate-rise">
        {step === 0 && (
          <section className="space-y-6">
            <header>
              <p className="eyebrow mb-2">Hoş geldiniz{firstName ? `, ${firstName}` : ""}</p>
              <h1 className="font-serif text-3xl text-slate-50">Sizi tanıyalım</h1>
              <p className="mt-2 text-sm text-slate-400">
                Brifinglerinizi kişiselleştirmek için birkaç bilgi.
              </p>
            </header>
            <div className="space-y-4">
              <Field label="Ad Soyad" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Onur Yılmaz" />
              <Field label="Şirket" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Zeniq" />
              <Field
                label="Ünvan"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="CEO, Genel Müdür, Kurucu Ortak…"
              />
            </div>
          </section>
        )}

        {step === 1 && (
          <section className="space-y-6">
            <header>
              <p className="eyebrow mb-2">{selected.size} konu seçildi</p>
              <h1 className="font-serif text-3xl text-slate-50">Neleri takip edelim?</h1>
              <p className="mt-2 text-sm text-slate-400">
                Her sabah yalnızca seçtiğiniz konulardaki kritik gelişmeleri süzeriz. En az bir tane seçin.
              </p>
            </header>
            {topics && <TopicPicker topics={topics} selected={selected} onToggle={toggleTopic} />}
          </section>
        )}

        {step === 2 && (
          <section className="space-y-6">
            <header>
              <p className="eyebrow mb-2">Son adım</p>
              <h1 className="font-serif text-3xl text-slate-50">Nasıl teslim edelim?</h1>
            </header>

            <div>
              <p className="mb-3 text-sm font-medium text-slate-300">Teslimat kanalı</p>
              <div className="grid gap-2.5 sm:grid-cols-3">
                {(
                  [
                    { id: "web", label: "Web paneli", icon: Globe, note: "Her zaman açık" },
                    { id: "slack", label: "Slack", icon: MessageSquare, note: "Faz 4'te" },
                    { id: "pdf", label: "PDF rapor", icon: FileText, note: "Faz 4'te" },
                  ] as const
                ).map(({ id, label, icon: Icon, note }) => {
                  const on = channels.has(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleChannel(id)}
                      aria-pressed={on}
                      className={`flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition-all ${
                        on ? "border-gold/60 bg-gold/10" : "border-slate-700/50 bg-ink-800/40 hover:border-slate-500/70"
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${on ? "text-gold" : "text-slate-400"}`} />
                      <span className={`text-sm ${on ? "text-white" : "text-slate-300"}`}>{label}</span>
                      <span className="text-xs text-slate-500">{note}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="max-w-xs">
              <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-300">
                <Clock className="h-4 w-4 text-slate-400" /> Teslimat saati (TRT)
              </label>
              <input
                type="time"
                value={deliveryTime}
                onChange={(e) => setDeliveryTime(e.target.value)}
                className="w-full rounded-lg border border-slate-700/60 bg-ink-800/60 px-3.5 py-2.5 text-sm text-slate-100 focus:border-gold/50 focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-700/50 bg-ink-800/40 p-4 text-sm text-slate-400">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-gold"
              />
              <span>
                <strong className="text-slate-200">KVKK aydınlatma metnini</strong> okudum; haber
                tercihlerimin ve profil bilgilerimin brifing üretimi için işlenmesine açık rıza
                veriyorum. Verilerim yalnızca bana aittir ve istediğimde silinebilir.
              </span>
            </label>
          </section>
        )}

        {error && (
          <p className="mt-5 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </p>
        )}

        {/* Gezinme */}
        <div className="mt-8 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className={step === 0 ? "invisible" : ""}
          >
            <ArrowLeft className="h-4 w-4" /> Geri
          </Button>

          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext}>
              Devam <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={finish} loading={saveOnboarding.isPending} disabled={!canNext}>
              <Hash className="h-4 w-4" /> Brifinglerimi kur
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
